import { useEffect, useState } from "react";
import {
  CurrencyCircleDollar, Lightning, Megaphone, UserPlus, Percent, Clock, ShieldCheck,
  Buildings, MapPin, Envelope, Phone, ArrowSquareOut, ChalkboardTeacher, Palette, ListChecks,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CENTER_MODULES, useOrganizationConfig } from "@/context/OrganizationConfigContext";
import { isSupabaseConfigured } from "@/lib/supabase";
import {
  TIMEZONE_OPTIONS,
  centsToDollars,
  dollarsToCents,
  getOrganization,
  updateOrganization,
} from "@/lib/admin/organizations";

/**
 * Admin · /admin/settings
 *
 * Org-wide defaults. Two groups:
 *  - Billing: defaults used by bulk tuition + invoice generation.
 *  - Features: high-level toggles that gate whole surfaces.
 */
export default function AdminSettings() {
  const { profile } = useAuth();
  const { modules, loading: modulesLoading, setModuleEnabled } = useOrganizationConfig();
  const orgId = profile?.organization_id;
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [moduleSaving, setModuleSaving] = useState({});

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const o = await getOrganization(orgId);
        setOrg(o);
        setForm(toForm(o));
      } catch (err) {
        toast.error(err.message || "Could not load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  const dirty = org && form && JSON.stringify(form) !== JSON.stringify(toForm(org));

  const onSave = async () => {
    if (!form) return;
    const patch = {
      display_name: form.display_name.trim(),
      timezone: form.timezone,
      contact_email: form.contact_email.trim(),
      contact_phone: form.contact_phone.trim(),
      monthly_tuition_default_cents: form.monthly_tuition_default
        ? dollarsToCents(form.monthly_tuition_default)
        : null,
      late_fee_cents: form.late_fee ? dollarsToCents(form.late_fee) : 0,
      late_fee_grace_days: Math.max(0, Math.min(90, parseInt(form.late_fee_grace_days, 10) || 0)),
      sibling_discount_percent: Math.max(0, Math.min(100, parseInt(form.sibling_discount_percent, 10) || 0)),
      autopay_enabled: form.autopay_enabled,
      feature_public_announcements: form.feature_public_announcements,
      feature_enrollment_open: form.feature_enrollment_open,
      enforce_attendance_clearance: form.enforce_attendance_clearance,
    };
    setSaving(true);
    try {
      const updated = await updateOrganization(orgId, patch);
      setOrg(updated);
      setForm(toForm(updated));
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onModuleChange = async (moduleKey, enabled) => {
    setModuleSaving((previous) => ({ ...previous, [moduleKey]: true }));
    try {
      await setModuleEnabled(moduleKey, enabled);
      toast.success(enabled ? "Center module enabled" : "Center module disabled");
    } catch (error) {
      toast.error(error.message || "Could not update center module");
    } finally {
      setModuleSaving((previous) => ({ ...previous, [moduleKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!isSupabaseConfigured || !orgId || !form) {
    return (
      <section
        data-testid="admin-settings-live-center-required"
        className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center"
      >
        <h1 className="font-heading text-lg font-semibold text-neutral-900">
          Settings need a live center
        </h1>
        <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-neutral-600">
          Center identity and workflow configuration are available after HoneyAcorn connects to a center in a live environment. Demo mode keeps the rest of the admin workspace available for preview.
        </p>
      </section>
    );
  }

  const input =
    "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
  const inputMono = `${input} font-mono`;

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            System
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Settings
          </h1>
          <p className="mt-2 max-w-2xl text-neutral-600">
            Center identity, supported workflows, and family-billing defaults.
            HoneyAcorn product pricing remains separate and unconfigured.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-settings-save"
          disabled={saving || !dirty}
          onClick={onSave}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
      </section>

      {/* Organization profile */}
      <Section title="Organization profile" icon={<Buildings size={16} weight="duotone" />}>
        <Row>
          <Field label="Display name" hint="Shown to families across the parent app and emails.">
            <input
              data-testid="admin-settings-org-name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="Little Oak Daycare"
              className={input}
            />
          </Field>
          <Field
            label="Timezone"
            hint="Used for invoice cycles, attendance dates, and notification timing."
          >
            <select
              data-testid="admin-settings-org-timezone"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              className={input}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </Field>
        </Row>
        <Row>
          <Field
            label="Contact email"
            hint="Reply-to address on parent-facing emails."
            icon={<Envelope size={12} weight="duotone" />}
          >
            <input
              data-testid="admin-settings-org-contact-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="hello@oakdaycare.com"
              className={input}
            />
          </Field>
          <Field
            label="Contact phone"
            hint="Optional — surfaced on the public site and contact pages."
            icon={<Phone size={12} weight="duotone" />}
          >
            <input
              data-testid="admin-settings-org-contact-phone"
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="+1 555 0100"
              className={input}
            />
          </Field>
        </Row>
        <p className="text-[11px] text-neutral-500">
          For logo, brand colors, and the full org profile, visit{" "}
          <Link
            to="/admin/organizations"
            className="font-semibold text-primary-hover hover:underline"
            data-testid="admin-settings-org-profile-link"
          >
            Organization branding
          </Link>.
        </p>
      </Section>

      {/* Billing defaults */}
      <Section title="Family billing defaults" icon={<CurrencyCircleDollar size={16} weight="duotone" />}>
        <Row>
          <Field
            label="Default monthly tuition"
            hint="Used by Bulk Tuition when no per-classroom or per-family override is set."
          >
            <div className="flex">
              <span className="flex items-center rounded-l-xl border border-r-0 border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500">$</span>
              <input
                data-testid="admin-settings-monthly-tuition"
                value={form.monthly_tuition_default}
                onChange={(e) => setForm({ ...form, monthly_tuition_default: e.target.value })}
                placeholder="0.00"
                inputMode="decimal"
                className={`${inputMono} rounded-l-none`}
              />
            </div>
          </Field>
          <Field
            label="Sibling discount"
            hint="Applied automatically when a family has 2+ active children."
            icon={<Percent size={12} weight="duotone" />}
          >
            <div className="flex">
              <input
                data-testid="admin-settings-sibling-discount"
                value={form.sibling_discount_percent}
                onChange={(e) => setForm({ ...form, sibling_discount_percent: e.target.value })}
                type="number"
                min="0"
                max="100"
                className={`${inputMono} rounded-r-none`}
              />
              <span className="flex items-center rounded-r-xl border border-l-0 border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500">%</span>
            </div>
          </Field>
        </Row>
        <Row>
          <Field label="Late fee amount" hint="Flat fee added when an invoice is overdue.">
            <div className="flex">
              <span className="flex items-center rounded-l-xl border border-r-0 border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500">$</span>
              <input
                data-testid="admin-settings-late-fee"
                value={form.late_fee}
                onChange={(e) => setForm({ ...form, late_fee: e.target.value })}
                placeholder="0.00"
                inputMode="decimal"
                className={`${inputMono} rounded-l-none`}
              />
            </div>
          </Field>
          <Field
            label="Grace period"
            hint="Days after the due date before a late fee is applied."
            icon={<Clock size={12} weight="duotone" />}
          >
            <div className="flex">
              <input
                data-testid="admin-settings-late-fee-grace"
                value={form.late_fee_grace_days}
                onChange={(e) => setForm({ ...form, late_fee_grace_days: e.target.value })}
                type="number"
                min="0"
                max="90"
                className={`${inputMono} rounded-r-none`}
              />
              <span className="flex items-center rounded-r-xl border border-l-0 border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500">days</span>
            </div>
          </Field>
        </Row>
      </Section>

      {/* Supported workflow controls */}
      <Section title="Supported workflow controls" icon={<Lightning size={16} weight="duotone" />}>
        <ToggleRow
          testId="admin-settings-toggle-autopay"
          icon={<Lightning size={16} weight="duotone" />}
          title="Stripe Autopay"
          desc="Lets parents enroll in automatic tuition payments. Turn off to hide Autopay setup across parent & admin surfaces."
          checked={form.autopay_enabled}
          onChange={(v) => setForm({ ...form, autopay_enabled: v })}
        />
        <ToggleRow
          testId="admin-settings-toggle-announcements"
          icon={<Megaphone size={16} weight="duotone" />}
          title="Parent announcements card"
          desc="Surfaces the 3 most recent published announcements on parent home. Published content still exists even when this is off."
          checked={form.feature_public_announcements}
          onChange={(v) => setForm({ ...form, feature_public_announcements: v })}
        />
        <ToggleRow
          testId="admin-settings-toggle-enrollment"
          icon={<UserPlus size={16} weight="duotone" />}
          title="Public enrollment form"
          desc="Allows families to submit new enrollment requests from the parent portal. Turn off when fully enrolled."
          checked={form.feature_enrollment_open}
          onChange={(v) => setForm({ ...form, feature_enrollment_open: v })}
        />
        <ToggleRow
          testId="admin-settings-toggle-enforce-clearance"
          icon={<ShieldCheck size={16} weight="duotone" />}
          title="Block attendance on compliance gaps"
          desc="When ON, teachers cannot mark a child present or late if required blocking health records are missing. Admins can still override with a reason. Off by default — recommended to run with informational signals for at least two weeks before turning this on."
          checked={form.enforce_attendance_clearance}
          onChange={(v) => setForm({ ...form, enforce_attendance_clearance: v })}
        />
      </Section>

      <Section title="Center modules" icon={<Palette size={16} weight="duotone" />}>
        <p className="-mt-1 text-xs leading-5 text-neutral-500">
          Choose the HoneyAcorn workflows this center supports. These toggles only control center capabilities and navigation; they do not create a HoneyAcorn plan, price, subscription, trial, or paywall.
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          {CENTER_MODULES.map((module) => (
            <ToggleRow
              key={module.key}
              testId={`admin-settings-module-${module.key}`}
              icon={<Lightning size={16} weight="duotone" />}
              title={module.label}
              desc={
                module.key === "daily_activity" && modules.learning !== false
                  ? "Daily reports, parent updates, and the entry point for Learning observations. Disable Learning observations before disabling this required workflow."
                  : module.description
              }
              checked={modules[module.key] !== false}
              disabled={
                modulesLoading
                || moduleSaving[module.key]
                || (module.key === "learning" && modules.daily_activity === false)
                || (module.key === "daily_activity" && modules.learning !== false)
              }
              onChange={(enabled) => onModuleChange(module.key, enabled)}
            />
          ))}
        </div>
      </Section>

      {/* Quick links — discoverability for related catalog surfaces. */}
      <Section title="Configure also" icon={<ListChecks size={16} weight="duotone" />}>
        <p className="-mt-1 text-xs text-neutral-500">
          Other operational settings live on dedicated pages so they have room to breathe.
        </p>
        <ul className="space-y-2">
          <QuickLink
            to="/admin/compliance?tab=catalog"
            testId="admin-settings-quicklink-catalog"
            icon={<ShieldCheck size={16} weight="duotone" />}
            title="Compliance catalog"
            desc="Add or retire staff certification types and child health record types."
          />
          <QuickLink
            to="/admin/classrooms"
            testId="admin-settings-quicklink-classrooms"
            icon={<ChalkboardTeacher size={16} weight="duotone" />}
            title="Classrooms & ratios"
            desc="Per-classroom capacity, age group, monthly tuition default, and required staff ratio."
          />
          <QuickLink
            to="/admin/organizations"
            testId="admin-settings-quicklink-brand"
            icon={<Palette size={16} weight="duotone" />}
            title="Branding & full profile"
            desc="Logo, brand colors, contact address, and slug used by the public site."
          />
        </ul>
      </Section>
    </div>
  );
}

function toForm(o) {
  return {
    display_name: o?.display_name || o?.name || "",
    timezone: o?.timezone || "UTC",
    contact_email: o?.contact_email || "",
    contact_phone: o?.contact_phone || "",
    monthly_tuition_default: centsToDollars(o?.monthly_tuition_default_cents),
    late_fee: centsToDollars(o?.late_fee_cents),
    late_fee_grace_days: String(o?.late_fee_grace_days ?? 0),
    sibling_discount_percent: String(o?.sibling_discount_percent ?? 0),
    autopay_enabled: o?.autopay_enabled ?? true,
    feature_public_announcements: o?.feature_public_announcements ?? true,
    feature_enrollment_open: o?.feature_enrollment_open ?? true,
    enforce_attendance_clearance: o?.enforce_attendance_clearance ?? false,
  };
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
      <p className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        <span className="text-primary-hover">{icon}</span>
        {title}
      </p>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
}

function Field({ label, icon, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {icon}
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[10px] text-neutral-400">{hint}</span>}
    </label>
  );
}

function ToggleRow({ icon, title, desc, checked, onChange, testId, disabled = false }) {
  return (
    <div
      data-testid={testId}
      data-checked={checked}
      className="flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 p-4"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-secondary/30 text-primary-hover">
          {icon}
        </span>
        <div>
          <p className="font-heading text-sm font-semibold text-neutral-900">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">{desc}</p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        data-testid={`${testId}-switch`}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-all disabled:cursor-wait disabled:opacity-50 ${
          checked ? "border-primary/50 bg-primary" : "border-neutral-200 bg-neutral-200"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}


function QuickLink({ to, testId, icon, title, desc }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testId}
        className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3 transition hover:border-primary/40 hover:bg-primary/[0.02]"
      >
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-secondary/30 text-primary-hover">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold text-neutral-900">{title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">{desc}</p>
        </div>
        <ArrowSquareOut size={14} weight="duotone" className="mt-1 flex-shrink-0 text-neutral-400" />
      </Link>
    </li>
  );
}
